import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchOrderSubsListRequest,
  orderSubsQueryRequest,
  orderSubsRequest,
  orderUpgradeRequest,
} from '@/lib/service/home';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { OrderModel } from '@/types/home';
import useCommentViewPremium from '@/lib/hooks/isPremium/useCommentViewPremium';

const cashMap = {
  JPY: 'JP¥',
  KRW: '₩',
  USD: '$',
  THB: '฿',
};

const right = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-4">
    <rect width="18" height="18" rx="9" fill="#38C13D" />
    <path d="M3.5 8.5L7.5 12.5L14 6" stroke="white" strokeWidth="2" />
  </svg>
);

const err = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-4">
    <rect x="1" y="1" width="18" height="18" rx="9" fill="white" stroke="#999999" strokeWidth="2" />
    <path
      d="M7 7L10 10M13 13L10 10M10 10L13 7L7 13"
      stroke="#999999"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const plans = [
  {
    name: 'Free',
    price: '0.00',
    price_year: '0.00',
    period: '/mo',
    button: 'Free',
    highlight: false,
    credits: 0,
    maxCommentsPerPost: 15,
    maxCommentsPerLoad: 15,
    commentSearch: true,
    exportComments: false,
    sentiment: false,
    insights: false,
    postCount: false,
    avgComments: false,
    avgLikes: false,
    avgEngagement: false,
    avgDailyPosts: false,
    features: [
      { label: 'Comment Search', checked: true },
      { label: 'Export Comments', checked: false },
    ],
    sentimentAnalysis: [
      { label: 'Comment Sentiment', checked: false },
      { label: 'Comment Insights', checked: false },
    ],
    accountAnalysis: [
      { label: 'Total Posts (Last 30 Days)', checked: false },
      { label: 'Avg. Comments', checked: false },
      { label: 'Avg. Likes', checked: false },
      { label: 'Avg. Engagement', checked: false },
      { label: 'Avg. Daily Posts', checked: false },
    ],
  },
  {
    name: 'Basic',
    price: '6.99',
    price_year: '4.16',
    period: '/mo',
    button: 'Subscribe',
    highlight: false,
    credits: 200,
    maxCommentsPerPost: 'Unlimited',
    maxCommentsPerLoad: 30,
    features: [
      { label: 'Comment Search', checked: true },
      { label: 'Export Comments', checked: true },
    ],
    sentimentAnalysis: [
      { label: 'Comment Sentiment', checked: false },
      { label: 'Comment Insights', checked: false },
    ],
    accountAnalysis: [
      { label: 'Total Posts (Last 30 Days)', checked: false },
      { label: 'Avg. Comments', checked: false },
      { label: 'Avg. Likes', checked: false },
      { label: 'Avg. Engagement', checked: false },
      { label: 'Avg. Daily Posts', checked: false },
    ],
  },
  {
    name: 'Pro',
    price: '19.99',
    price_year: '13.33',
    period: '/mo',
    button: 'Subscribe',
    highlight: true,
    credits: 2000,
    maxCommentsPerPost: 'Unlimited',
    maxCommentsPerLoad: 120,
    features: [
      { label: 'Comment Search', checked: true },
      { label: 'Export Comments', checked: true },
    ],
    sentimentAnalysis: [
      { label: 'Comment Sentiment', checked: true },
      { label: 'Comment Insights', checked: true },
    ],
    accountAnalysis: [
      { label: 'Total Posts (Last 30 Days)', checked: true },
      { label: 'Avg. Comments', checked: true },
      { label: 'Avg. Likes', checked: true },
      { label: 'Avg. Engagement', checked: true },
      { label: 'Avg. Daily Posts', checked: true },
    ],
  },
  {
    name: 'Elite',
    price: '69.99',
    price_year: '46.66',
    period: '/mo',
    button: 'Subscribe',
    highlight: false,
    credits: 20000,
    maxCommentsPerPost: 'Unlimited',
    maxCommentsPerLoad: 600,
    features: [
      { label: 'Comment Search', checked: true },
      { label: 'Export Comments', checked: true },
    ],
    sentimentAnalysis: [
      { label: 'Comment Sentiment', checked: true },
      { label: 'Comment Insights', checked: true },
    ],
    accountAnalysis: [
      { label: 'Total Posts (Last 30 Days)', checked: true },
      { label: 'Avg. Comments', checked: true },
      { label: 'Avg. Likes', checked: true },
      { label: 'Avg. Engagement', checked: true },
      { label: 'Avg. Daily Posts', checked: true },
    ],
  },
];

function PlanCard({
  item_id,
  item,
  showPlans,
  plan,
  tab,
  currentUserPlan,
  onSubmit,
  index,
  example,
}: {
  item_id: number;
  item: any;
  showPlans: any[];
  plan: any;
  tab: 'monthly' | 'annually';
  currentUserPlan: any;
  onSubmit: (itemId: number, actionType: 'subscribe' | 'upgrade') => Promise<void>;
  index: number;
  example: any;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation('common');

  const canPurchase = () => {
    if (plan.name === 'Free') {
      return false;
    }
    if (currentUserPlan) {
      const arr = showPlans.filter((p) => p.item_id === currentUserPlan.item_id);
      if (arr.length > 0) {
        if (currentUserPlan.item_id < item_id) {
          return true;
        }
        return false;
      } else {
        return false;
      }
    }
    return true;
  };

  const getActionType = () => {
    if (!currentUserPlan) {
      return 'subscribe';
    }
    return 'upgrade';
  };

  const getButtonText = () => {
    if (plan.name === 'Free') {
      return 'Free';
    }
    if (item_id == currentUserPlan?.item_id) {
      return 'Current Subscription';
    }

    const hasItem = showPlans.filter((p) => p.item_id == currentUserPlan?.item_id).length > 0;

    if (item_id > currentUserPlan?.item_id && hasItem) {
      return 'Upgrade';
    }
    return 'Subscribe';
  };

  const canBuy = canPurchase();
  const actionType = getActionType();
  const buttonText = getButtonText();

  const getPrice = () => {
    if (index === 0) {
      return 0;
    }
    if (tab === 'monthly') {
      return item?.amount;
    }
    if (item?.currency == 'USD') {
      return (item?.amount / 12).toFixed(2);
    } else if (item?.currency == 'THB') {
      return Math.floor(Number((item?.amount / 12).toFixed(2)));
    }
    return (item?.amount / 12).toFixed(0);
  };

  const isHighlight = useMemo(() => {
    if (currentUserPlan?.item_id) {
      return false;
    } else {
      return plan.highlight;
    }
  }, [plan, currentUserPlan]);

  return (
    <div
      className={`flex flex-col cursor-pointer rounded-2xl border bg-white px-6 pt-8 pb-4 shadow-sm min-w-[260px] max-w-[320px] flex-1 ${
        isHighlight ? 'border-[#6366F1]' : 'border-gray-200'
      }`}
    >
      <div className={`text-center text-xl font-bold mb-2 ${isHighlight ? 'text-green-500' : 'text-black'}`}>
        {t(plan.name)}
      </div>
      <div className="flex items-end justify-center">
        {/* 显示货币符号，修复类型报错，确保只用已知的货币类型 */}
        <span className="text-black inline-block pb-2 font-bold pr-1">
          {index === 0
            ? example?.currency && cashMap.hasOwnProperty(example.currency)
              ? cashMap[example.currency as keyof typeof cashMap]
              : ''
            : item?.currency && cashMap.hasOwnProperty(item.currency)
              ? cashMap[item.currency as keyof typeof cashMap]
              : ''}
        </span>
        {/* 显示金额 */}
        <span className="text-3xl font-bold">{getPrice()}</span>
        {/* 显示周期 */}
        <span className="text-gray-400 ml-1 inline-block pb-2">{t(plan.period)}</span>
      </div>
      <button
        className={`w-full py-2 rounded-lg my-5 text-sm font-semibold ${
          !canBuy || isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-indigo-500 text-white hover:bg-indigo-600'
        }`}
        disabled={!canBuy || isLoading}
        onClick={async () => {
          if (item_id && canBuy && !isLoading) {
            setIsLoading(true);
            try {
              await onSubmit(item_id, actionType);
            } finally {
              setIsLoading(false);
            }
          }
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          t(buttonText)
        )}
        {item?.subs_id}
      </button>
      <div className="text-xs text-gray-500 mb-2">
        <span className="text-sm text-gray-900 font-bold">{t('Credits')}</span>{' '}
        <span className="float-right text-[#725CFF] font-medium">{plan.credits}</span>
      </div>
      <div className="text-sm text-black mb-2">
        <div className="flex justify-between mb-2 ">
          <span>{t('Max Comments per Post')}</span>
          <span>{t(plan.maxCommentsPerPost)}</span>
        </div>
      </div>
      <div className="text-sm text-black mb-2">
        {plan.features.map((f: any, i: number) => (
          <div className="flex justify-between items-center mb-2" key={i}>
            <span>{t(f.label)}</span>
            {f.checked ? (
              <span className="text-green-500 text-lg">{right}</span>
            ) : (
              <span className="text-gray-400 text-lg">{err}</span>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 my-4"></div>
      <div className="text-sm text-gray-900 font-bold mt-4 mb-2">{t('Sentiment Breakdown')}</div>
      <div className="text-sm text-black mb-2">
        {plan.sentimentAnalysis.map((f: any, i: number) => (
          <div className="flex justify-between items-center mb-2" key={i}>
            <span>{t(f.label)}</span>
            {f.checked ? (
              <span className="text-green-500 text-lg">{right}</span>
            ) : (
              <span className="text-gray-400 text-lg">{err}</span>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 my-4"></div>
      <div className="text-sm text-gray-900 font-bold mt-4 mb-2">{t('Account Performance')}</div>
      <div className="text-sm text-black">
        {plan.accountAnalysis.map((f: any, i: number) => (
          <div className="flex justify-between items-center mb-2" key={i}>
            <span>{t(f.label)}</span>
            {f.checked ? (
              <span className="text-green-500 text-lg">{right}</span>
            ) : (
              <span className="text-gray-400 text-lg">{err}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CommentsPlan({
  data,
  locale,
  guideToRegisterOrLogin,
  isLogin,
}: {
  data: any;
  locale: string;
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
}) {
  const [tab, setTab] = useState<'monthly' | 'annually'>('annually');
  const [currentUserPlan, setCurrentUserPlan] = useState(null);
  const { t } = useTranslation('common');
  const [planList, setPlanList] = useState([]);
  const { isPremium } = useCommentViewPremium();

  useEffect(() => {
    if (typeof window !== 'undefined' && navigator.language) {
      const browserLanguage = navigator.language;
      const prefix = browserLanguage.split('-')[0];
      orderSubsQueryRequest('post_comment', prefix ?? 'en').then((res) => {
        if (res.code === 0) {
          setPlanList(res.data);
        }
      });
    }
  }, [isPremium]);
  useEffect(() => {
    if (isLogin) {
      fetchOrderSubsListRequest('post_comment', locale).then((res: any) => {
        if (res.code === 0 && res.data?.[0]?.status === 'normal') {
          setCurrentUserPlan(res.data[0]);
        } else {
          setCurrentUserPlan(null);
        }
      });
    }
  }, [isLogin, isPremium]);

  const showPlans = useMemo(() => {
    if (tab === 'monthly') {
      return planList.filter((_, index) => index % 2 === 0);
    }
    return planList.filter((_, index) => index % 2 === 1);
  }, [planList, tab]);

  const loadScripePay = async (orderModel: OrderModel) => {
    window.open(orderModel?.pay_link, '_self');
  };

  const buyClick = async (itemId: number, actionType: 'subscribe' | 'upgrade', subs_id?: string) => {
    if (!isLogin) {
      guideToRegisterOrLogin();
      return;
    }
    try {
      if (actionType === 'subscribe') {
        const res = await orderSubsRequest(
          {
            item_id: itemId,
            redirect: '/instagram-viewer/post-comments',
          },
          locale,
          { isPremium, bizFuncName: 'comment_viewer' }
        );

        if (res.code === 0) {
          loadScripePay(res.data);
        } else {
          message.error(res.message);
        }
      } else {
        const res = await orderUpgradeRequest(currentUserPlan.subs_id, itemId, '', locale, {
          isPremium,
          bizFuncName: 'comment_viewer',
        });
        if (res.code === 0) {
          loadScripePay(res.data);
        } else {
          message.error(res.message);
        }
      }
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  return (
    <div className="w-full flex flex-col items-center mt-[100px]">
      <h2 className="text-4xl font-bold mb-2 text-center scroll-mt-20" id="pricing-title">
        {t('Pricing for Instagram Comment Viewer')}
      </h2>
      <div className="text-gray-500 text-center max-w-2xl mb-6 text-sm">
        {t(
          'Dolphin Radar lets you quickly view and export comments from posts, Reels, and photos — with options for advanced insights when you need them. Whether you’re researching engagement, tracking audience sentiment, or simply keeping up with conversations, it’s all in one convenient tool.'
        )}
      </div>
      <div className="relative flex items-center mb-8 p-1 bg-[#E3E6F3] rounded-full">
        <div className="flex rounded-full w-[260px] h-8 relative">
          <div
            className={`absolute top-0 left-0 h-8 w-1/2 rounded-full bg-white  transition-transform duration-300 ease-in-out z-10`}
            style={{
              transform: tab === 'monthly' ? 'translateX(0)' : 'translateX(100%)',
            }}
          />
          <button
            className={`flex-1 z-20 relative h-8 flex items-center justify-center font-semibold text-sm transition-colors duration-200 rounded-full ${
              tab === 'monthly' ? 'text-indigo-600' : 'text-gray-400'
            }`}
            onClick={() => setTab('monthly')}
          >
            {t('Monthly')}
          </button>
          <button
            className={`flex-1 z-20 relative h-8 flex items-center justify-center font-semibold text-sm transition-colors duration-200 rounded-full ${
              tab === 'annually' ? 'text-indigo-600' : 'text-gray-400'
            }`}
            onClick={() => setTab('annually')}
          >
            {t('Annually')}
          </button>
        </div>
        <span className="absolute -top-3 -right-8 px-2 py-0.5 z-20 rounded-full text-white bg-red-500 text-xs font-semibold ">
          {t('Free 4mo')}
        </span>
      </div>
      <div className="w-full px-0 lg:px-2">
        <div className="flex gap-6 w-full max-w-6xl overflow-x-auto">
          {plans.map((plan, index) => (
            <PlanCard
              showPlans={showPlans}
              item_id={index === 0 ? null : showPlans[index - 1]?.item_id}
              item={index === 0 ? null : showPlans[index - 1]}
              plan={plan}
              key={plan.name}
              tab={tab}
              index={index}
              currentUserPlan={currentUserPlan}
              onSubmit={buyClick}
              example={showPlans[index + 1]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const PlanContent = ({
  data,
  locale,
  guideToRegisterOrLogin,
  isLogin,
}: {
  data?: any;
  locale: string;
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
}) => {
  return <CommentsPlan data={data} locale={locale} guideToRegisterOrLogin={guideToRegisterOrLogin} isLogin={isLogin} />;
};

export default PlanContent;
